import { z } from 'zod';

/**
 * @aiPurpose locked catalog of CRM Deal stage codes (#1087).
 *   Seeded by `20260524010000_add_crm_deals_foundation`. No admin edit
 *   pathway ships in #1087; localization of `displayName` lives in
 *   the i18n layer.
 * @aiInvariant codes, kinds, and seeded order are LOCKED. Adding a new
 *   stage requires a paired backfill migration AND a coordinated
 *   shared-schema update. The five canonical codes mirror the Prisma
 *   `DealStage.code` catalog and the `DealStageKind` enum.
 */
export const DEAL_STAGE_CODES = [
  'qualification',
  'proposal',
  'negotiation',
  'won',
  'lost',
] as const;
export type DealStageCode = (typeof DEAL_STAGE_CODES)[number];
export const DealStageCodeSchema = z.enum(DEAL_STAGE_CODES);

export const ACTIVE_DEAL_STAGE_CODES = [
  'qualification',
  'proposal',
  'negotiation',
] as const satisfies ReadonlyArray<DealStageCode>;
export type ActiveDealStageCode = (typeof ACTIVE_DEAL_STAGE_CODES)[number];

export const WON_DEAL_STAGE_CODE = 'won' as const satisfies DealStageCode;
export const LOST_DEAL_STAGE_CODE = 'lost' as const satisfies DealStageCode;

export const TERMINAL_DEAL_STAGE_CODES = [
  WON_DEAL_STAGE_CODE,
  LOST_DEAL_STAGE_CODE,
] as const satisfies ReadonlyArray<DealStageCode>;
export type TerminalDealStageCode = (typeof TERMINAL_DEAL_STAGE_CODES)[number];

/**
 * @aiPurpose lifecycle class of a Deal stage. Mirrors the Prisma
 *   `DealStageKind` enum. `WON` and `LOST` are split (not collapsed
 *   into a single `TERMINAL`) so the reporting layer can group
 *   "won-by-stage" / "lost-by-stage" counts without a secondary
 *   `outcome` lookup.
 */
export enum DealStageKind {
  ACTIVE = 'ACTIVE',
  WON = 'WON',
  LOST = 'LOST',
}
export const DealStageKindSchema = z.nativeEnum(DealStageKind);

const ACTIVE_SET: ReadonlySet<DealStageCode> = new Set(ACTIVE_DEAL_STAGE_CODES);
const TERMINAL_SET: ReadonlySet<DealStageCode> = new Set(TERMINAL_DEAL_STAGE_CODES);

export const isActiveDealStageCode = (code: DealStageCode): code is ActiveDealStageCode =>
  ACTIVE_SET.has(code);
export const isTerminalDealStageCode = (code: DealStageCode): code is TerminalDealStageCode =>
  TERMINAL_SET.has(code);

/**
 * @aiPurpose v1 Deal stage transition graph. Active stages may move
 *   among themselves and into the terminal `won` / `lost` stages;
 *   terminal stages do not transition out — a closed deal that needs
 *   re-opening is handled by an admin restore flow in a follow-up.
 */
export const canTransitionDealStage = (from: DealStageCode, to: DealStageCode): boolean => {
  if (from === to) return false;
  if (isTerminalDealStageCode(from)) return false;
  return true;
};

export const DEAL_STAGE_DISPLAY_NAME_MAX = 128;

/**
 * @aiPurpose wire projection of a `DealStage` catalog row.
 */
export const DealStageSchema = z
  .object({
    code: DealStageCodeSchema,
    kind: DealStageKindSchema,
    displayName: z.string().min(1).max(DEAL_STAGE_DISPLAY_NAME_MAX),
    sortOrder: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type DealStage = z.infer<typeof DealStageSchema>;
